import { paginatedProducts } from '../../../../graphQL/queries/productQueries';
import { getGlobalProps } from './../../../../utils/queryHelpers';
import _get from 'lodash/get';
import _isFinite from 'lodash/isFinite';
import {
  CUBIC_CENTIMETERS_PER_CUBIC_METER,
  CUBIC_INCHES_PER_CUBIC_FOOT,
  METRIC_VOLUME,
  FREIGHT_CLASSES,
  PACKAGE_TYPE_PALLET
} from '../../../../constants/constants';

export const productSearch = async (ApolloClient, searchText) => {
  const { orgInfo } = getGlobalProps();

  try {
    const {
      data: {
        product: {
          paged: { results }
        }
      }
    } = await ApolloClient.query({
      query: paginatedProducts,
      variables: {
        limit: 10,
        offset: 0,
        orgId: orgInfo.orgId,
        searchText,
        includeExpired: false
      }
    });

    return Promise.resolve(results);
  } catch (error) {
    return Promise.reject(error);
  }
};
//this method cleans up code in Product to give the absolute path to a field
export const fieldMapping = productField => {
  const paths = {
    sku: 'product.sku',
    name: 'product.name',
    description: 'product.description',
    freightClass: 'product.freightInfo.freightClassId',
    packageType: 'product.packageTypeId',
    nmfc: 'product.freightInfo.nmfcItemCode',
    subcode: 'product.freightInfo.nmfcSubCode',
    quantity: '',
    weight: 'product.weightInfo.weight',
    value: 'product.valueInfo.value',
    pieceCount: 'product.pieceCount',
    isHazMat: 'product.isHazMat',
    contactFirstName: 'product.hazMat.contactFirstName',
    contactLastName: 'product.hazMat.contactLastName',
    contactPhone: 'product.hazMat.contactPhone',
    hazMatClassId: 'product.hazMat.hazMatClassId',
    hazMatPackingGroupId: 'product.hazMat.hazMatPackingGroupId',
    properShippingName: 'product.hazMat.properShippingName',
    unNumber: 'product.hazMat.unNumber'
  };
  return {
    sku: buildFieldPaths(productField, paths.sku),
    name: buildFieldPaths(productField, paths.name),
    description: buildFieldPaths(productField, paths.description),
    freightClass: buildFieldPaths(productField, paths.freightClass),
    packageType: buildFieldPaths(productField, paths.packageType),
    nmfc: buildFieldPaths(productField, paths.nmfc),
    subcode: buildFieldPaths(productField, paths.subcode),
    quantity: `${productField}.quantity`,
    weight: buildFieldPaths(productField, paths.weight),
    value: buildFieldPaths(productField, paths.value),
    pieceCount: buildFieldPaths(productField, paths.pieceCount),
    isHazMat: buildFieldPaths(productField, paths.isHazMat),
    contactFirstName: buildFieldPaths(productField, paths.contactFirstName),
    contactLastName: buildFieldPaths(productField, paths.contactLastName),
    contactPhone: buildFieldPaths(productField, paths.contactPhone),
    hazMatClassId: buildFieldPaths(productField, paths.hazMatClassId),
    hazMatPackingGroupId: buildFieldPaths(
      productField,
      paths.hazMatPackingGroupId
    ),
    properShippingName: buildFieldPaths(productField, paths.properShippingName),
    unNumber: buildFieldPaths(productField, paths.unNumber)
  };
};
export const buildFieldPaths = (prefix, path) => ({
  fullPath: `${prefix}.${path}`,
  path
});
export const calcUnitTotal = unit => {
  return (
    Number(unit.unitCount) *
    unit.shipmentItems.reduce((total, item) => {
      const value = Number(_get(item, 'product.valueInfo.value', 0));
      const productValue = _isFinite(value) ? Number(value) : 0;
      return total + Number(item.quantity) * productValue;
    }, 0)
  );
};
export const calcUnitDensity = unit => {
  if (!unit.dimensionInfo || !unit.actualWeightInfo) return 0;

  const length = Number(_get(unit, 'dimensionInfo.length', 0));
  const width = Number(_get(unit, 'dimensionInfo.width', 0));
  const height = Number(_get(unit, 'dimensionInfo.height', 0));
  const weight = Number(_get(unit, 'actualWeightInfo.weight', 0));

  if (!length || !width || !height || !weight) {
    return 0;
  }

  // we've got everything we need to calculate it, do it.  Take into account
  // metric vs. imperial
  return (
    weight /
    ((length * width * height) /
      (unit.volumeUnit === METRIC_VOLUME
        ? CUBIC_CENTIMETERS_PER_CUBIC_METER
        : CUBIC_INCHES_PER_CUBIC_FOOT))
  );
};

export const calcUnitVolume = unit => {
  if (!unit.dimensionInfo) return 0;

  const length = Number(_get(unit, 'dimensionInfo.length', 0));
  const width = Number(_get(unit, 'dimensionInfo.width', 0));
  const height = Number(_get(unit, 'dimensionInfo.height', 0));
  const quantity = Number(_get(unit, 'unitCount', 0));

  if (!length || !width || !height || !quantity) {
    return 0;
  }

  // we've got everything we need to calculate it, do it.  Take into
  // account metric vs. imperial
  return (
    ((length * width * height) /
      (unit.volumeUnit === METRIC_VOLUME
        ? CUBIC_CENTIMETERS_PER_CUBIC_METER
        : CUBIC_INCHES_PER_CUBIC_FOOT)) *
    quantity
  );
};

export const calcClass = density => {
  // Class calculation taken from:
  // https://cerasis.com/2013/11/20/freight-class/

  let freightClass = FREIGHT_CLASSES.CLASS_50;
  switch (true) {
    case density < 1:
      freightClass = FREIGHT_CLASSES.CLASS_500;
      break;
    case density <= 2:
      freightClass = FREIGHT_CLASSES.CLASS_400;
      break;
    case density <= 3:
      freightClass = FREIGHT_CLASSES.CLASS_300;
      break;
    case density <= 4:
      freightClass = FREIGHT_CLASSES.CLASS_250;
      break;
    case density <= 5:
      freightClass = FREIGHT_CLASSES.CLASS_200;
      break;
    case density <= 6:
      freightClass = FREIGHT_CLASSES.CLASS_175;
      break;
    case density <= 7:
      freightClass = FREIGHT_CLASSES.CLASS_150;
      break;
    case density <= 8:
      freightClass = FREIGHT_CLASSES.CLASS_125;
      break;
    case density <= 9:
      freightClass = FREIGHT_CLASSES.CLASS_110;
      break;
    case density <= 10.5:
      freightClass = FREIGHT_CLASSES.CLASS_100;
      break;
    case density <= 12:
      freightClass = FREIGHT_CLASSES.CLASS_92_5;
      break;
    case density <= 13.5:
      freightClass = FREIGHT_CLASSES.CLASS_85;
      break;
    case density <= 15:
      freightClass = FREIGHT_CLASSES.CLASS_77_5;
      break;
    case density <= 22.5:
      freightClass = FREIGHT_CLASSES.CLASS_70;
      break;
    case density <= 30:
      freightClass = FREIGHT_CLASSES.CLASS_65;
      break;
    case density <= 35:
      freightClass = FREIGHT_CLASSES.CLASS_60;
      break;
    case density <= 50:
      freightClass = FREIGHT_CLASSES.CLASS_55;
      break;
    default:
      break;
  }
  return freightClass;
};

export const calcTotalWeight = handlingUnits => {
  return handlingUnits.reduce(
    (total, unit) => total + parseInt(unit.actualWeightInfo.weight, 10),
    0
  );
};

export const calcTotalPallets = handlingUnits => {
  return handlingUnits.reduce(
    (total, unit) =>
      total + (unit.packageTypeId === PACKAGE_TYPE_PALLET ? 1 : 0),
    0
  );
};
